import { useCallback, useEffect, useState } from 'react';

export const ONBOARDING_TOUR_DISMISS_KEY = 'eraf.onboardingTour.dismissed';

export interface UseOnboardingTourResult {
  isOpen: boolean;
  dismiss: () => void;
}

/**
 * queue #669 §UAT-2026-07-09-R669 §R669-4 — 온보딩 투어 게이팅 훅.
 *
 * `localStorage['eraf.onboardingTour.dismissed']` 로 최초 1회 자동 시작을 게이팅한다.
 * `forceStart` 는 '투어 다시 보기' 온디맨드 재시작(§R669-4) — dismiss 플래그를 무시하고
 * 강제로 1회 연다. 호출부(`BizIndexPage`)가 `location.state.startTour` 를 lazy 초기값으로
 * 한 번만 캡처해 넘기므로, 이 훅의 자동시작 effect 는 마운트 시 1회만 평가한다.
 *
 * 프라이빗 모드/쿼터 초과 등 localStorage 접근 실패는 try/catch 로 감싸 자동 시작을 조용히
 * 스킵한다(크래시 없음 — `AdoptionChecklist` §R660-5 폴백 패턴 계승).
 */
export function useOnboardingTour(forceStart: boolean): UseOnboardingTourResult {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (forceStart) {
      setIsOpen(true);
      return undefined;
    }

    let dismissed = true;
    try {
      dismissed = window.localStorage.getItem(ONBOARDING_TOUR_DISMISS_KEY) === 'true';
    } catch {
      dismissed = true; // 접근 불가 — 자동 시작 스킵(graceful)
    }
    if (dismissed) return undefined;

    // 대상 렌더 보장을 위한 짧은 지연(rAF) 후 자동 시작
    const id = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(id);
    // forceStart 는 호출부에서 마운트 시점 값으로 고정되어 넘어오므로 최초 1회만 평가한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    try {
      window.localStorage.setItem(ONBOARDING_TOUR_DISMISS_KEY, 'true');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useOnboardingTour] localStorage 저장 실패 — dismiss 상태가 지속되지 않습니다', e);
    }
  }, []);

  return { isOpen, dismiss };
}
